import { useState } from "react";
import { toast } from "sonner";
import { Trash2, X, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

/**
 * Superadmin-only danger button that mass-deletes a whitelisted collection
 * after a toggle + PIN confirmation. Renders nothing for non-superadmin.
 *
 * Props:
 *   collection : one of "patients" | "direct_bills" | "direct_bills_vault"
 *                | "admissions" | "inventory" | "inventory_moves"
 *                | "sms_log" | "whatsapp_log" | "therapist_schedule" | "counters"
 *   label      : human name shown in the button + confirm modal (e.g. "patients")
 *   onDone     : optional callback fired after successful purge (to reload data)
 */
export default function PurgeDataButton({ collection, label, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "superadmin") return null;

  const reset = () => { setConfirmed(false); setPin(""); setBusy(false); };
  const close = () => { setOpen(false); reset(); };

  const submit = async () => {
    if (!confirmed) { toast.error("Toggle the confirm switch first"); return; }
    if (!pin.trim()) { toast.error("Enter the superadmin PIN"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge", { collection, confirm_pin: pin.trim() });
      toast.success(`Purged ${data.deleted} record${data.deleted === 1 ? "" : "s"} from ${label}`);
      close();
      if (onDone) onDone();
    } catch (err) {
      toast.error(formatApiError(err, "Purge failed"));
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`purge-btn-${collection}`}
        className="h-10 px-3 rounded-lg border border-[#C25946] text-[#C25946] text-xs font-bold uppercase tracking-wider hover:bg-[#FBEDE9] inline-flex items-center gap-2"
        title={`Superadmin: permanently delete all ${label}`}
      >
        <Trash2 className="h-3.5 w-3.5" strokeWidth={2} /> Purge {label}
      </button>

      {open && (
        <div className="fixed inset-0 bg-[#2A2624]/70 z-[70] flex items-center justify-center p-4" onClick={close} data-testid={`purge-modal-${collection}`}>
          <div className="bg-white w-full max-w-md rounded-lg border-2 border-[#C25946] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-[#C25946]" strokeWidth={1.75} />
                <div className="font-display text-xl text-[#C25946]">Purge all {label}</div>
              </div>
              <button onClick={close} className="h-8 w-8 rounded-md border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>

            <div className="text-[13px] text-[#2A2624] mb-4 leading-relaxed">
              This will <strong>permanently delete every row</strong> in the <span className="font-mono text-[#C25946] bg-[#FBEDE9] px-1 rounded">{collection}</span> collection across all branches.
              This action cannot be undone. Inventory items and user accounts stay untouched.
            </div>

            <label className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#FBEDE9] border border-[#C25946]/40 cursor-pointer mb-4">
              <div className="text-sm font-semibold text-[#C25946]">I understand — delete all {label}</div>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                data-testid={`purge-toggle-${collection}`}
                className="h-5 w-9 accent-[#C25946]"
                style={{ accentColor: "#C25946" }}
              />
            </label>

            <div className="mb-4">
              <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 block">Superadmin PIN</label>
              <input
                type="password"
                data-testid={`purge-pin-${collection}`}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter shared PIN"
                autoComplete="new-password"
                className="w-full h-11 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#C25946]/40 font-mono"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={submit}
                disabled={busy || !confirmed || !pin.trim()}
                data-testid={`purge-confirm-${collection}`}
                className="flex-1 h-11 rounded-lg bg-[#C25946] text-white font-bold hover:bg-[#A6472F] disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                <Trash2 className="h-4 w-4" strokeWidth={2} /> {busy ? "Deleting…" : `Purge ${label}`}
              </button>
              <button onClick={close} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
